// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Button, Select, MenuItem, InputLabel, FormControl, Stack, Modal, Box, Typography, Grid, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Link, TablePagination, DialogContentText, IconButton, Divider, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocations } from '../hooks/useLocations';
import { useMachinesByLocation } from '../hooks/useMachinesByLocation';
import { useTechnicians } from '../hooks/useTechnicians';
import { getReports, addReport, updateReport, deleteReport, getMachines } from '../services/supabaseService';
import { supabase } from '../supabase';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';

const Reports: React.FC = () => {
  const [location, setLocation] = useState('');
  const [machine, setMachine] = useState('');
  const [cause, setCause] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [assignee, setAssignee] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [serviceNumber, setServiceNumber] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [reports, setReports] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editableReport, setEditableReport] = useState<any>(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const machinesForNewReport = useMachinesByLocation(selectedLocation);
  const machinesForEditReport = useMachinesByLocation(editableReport?.location || '');
  const locations = useLocations(); // Fetch locations from Supabase
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('Operation completed successfully.');
  const theme = useTheme();
  const [isRequiredFieldDialogOpen, setIsRequiredFieldDialogOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    location: '',
    startDate: '',
    endDate: '',
    assignee: '',
    status: '',
  });

  // Table header styling for dark mode
  const tableHeadStyle = theme.palette.mode === 'dark' ? {
    backgroundColor: '#424242', // Gray background in dark mode
    '& th': {
      color: '#fff', // White text in dark mode
      fontWeight: 'bold'
    }
  } : {};

  const fetchReports = async () => {
    try {


      // Fetch reports directly from Supabase
      const { data: reportsData, error } = await supabase
        .from('reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase fetch error:', error);
        throw error;
      }



      // Also fetch all machines once to have them available for lookup
      const allMachines = await getMachines();


      let reportsList = reportsData.map(report => {
        // Get the report data
        const reportData = {
          id: report.id,
          ...report.report_data
        };

        // If we have a machineId but no machineName, try to find the machine name
        if (reportData.machineId && !reportData.machineName) {
          // Find the machine in the list of all machines
          const machine = allMachines.find(m => m.id === reportData.machineId);
          if (machine) {
            reportData.machineName = machine.name;
          } else {
            // If we can't find the machine, use the ID as the name
            reportData.machineName = reportData.machineId;
          }
        }

        return reportData;
      });

      reportsList.sort((a, b) => new Date(b.date) - new Date(a.date));

      const filteredReports = reportsList.filter(report =>
        (!filters.location || report.location.includes(filters.location)) &&
        (!filters.startDate || new Date(report.date) >= new Date(filters.startDate)) &&
        (!filters.endDate || new Date(report.endDate) <= new Date(filters.endDate)) &&
        (!filters.assignee || report.assignee?.includes(filters.assignee)) &&
        (!filters.status ||
          (filters.status === "Resolved" && report.endDate) ||
          (filters.status === "In Progress" && !report.endDate && report.date)
        )
      );

      const finalReports = searchTerm ? filteredReports.filter(report =>
        Object.entries(report).some(([key, value]) =>
          typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase())
        )
      ) : filteredReports;


      setReports(finalReports);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
      alert("Failed to fetch reports: " + (error.message || "Unknown error"));
    }
  };

  useEffect(() => {
    fetchReports();
  }, [searchTerm, filters]);

  const getTicketDisplayAndLink = (ticketUrl) => {
    try {
      const url = new URL(ticketUrl);
      const pathSegments = url.pathname.split('/'); // Split the path into segments
      const displayPart = pathSegments.pop(); // Get the last segment as the ticket number
      const prefix = pathSegments.pop() || ''; // Get the second to last segment as the prefix, if available
      return {
        displayPart: prefix.toUpperCase() + '/' + displayPart,
        isValid: true
      };
    } catch {
      // If not a valid URL, return the input as is
      return { displayPart: ticketUrl, isValid: false };
    }
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const indexOfLastReport = (page + 1) * rowsPerPage;
  const indexOfFirstReport = indexOfLastReport - rowsPerPage;
  const currentReports = reports.slice(indexOfFirstReport, indexOfLastReport);

  const causes = ['Hardware', 'Software', 'Network'];
  const assignees = useTechnicians();

  const handleSubmit = async () => {
    if (!selectedLocation || !machine || !assignee || !cause || !startDate) {
      setIsRequiredFieldDialogOpen(true);
      return;
    }

    // Generate a UUID for the new report
    const reportId = crypto.randomUUID();

    // Set status based on whether there's an end date
    const reportStatus = endDate ? 'Up' : 'Down';

    const reportData = {
      assignee,
      cause,
      description,
      date: startDate,
      endDate,
      location: selectedLocation,
      machineId: machine, // assuming 'machine' state variable stores machineId
      machineName: machinesForNewReport.find(m => m.id === machine)?.name || machine, // Store machine name for display
      poNumber,
      serviceNumber,
      ticketNumber,
      status: reportStatus, // Use the determined status
      id: reportId // Use the UUID we generated
    };

    try {


      // Insert directly using supabase
      const { data, error } = await supabase
        .from('reports')
        .insert([{
          id: reportId,
          report_data: reportData,
          location: selectedLocation,
          date: startDate,
          end_date: endDate,
          machine: machine,
          status: reportStatus,
          type: cause
        }])
        .select();

      if (error) {
        console.error('Supabase create error:', error);
        throw error;
      }



      // Refresh the reports list to get the latest data from the server
      await fetchReports();

      // Clear form fields
      setSelectedLocation('');
      setMachine('');
      setCause('');
      setDescription('');
      setStartDate('');
      setEndDate('');
      setAssignee('');
      setPoNumber('');
      setServiceNumber('');
      setTicketNumber('');

      // Show success message
      setSuccessMessage('Report created successfully.');
      setSuccessDialogOpen(true);
      setTimeout(() => {
        setSuccessDialogOpen(false);
      }, 1500);
    } catch (error) {
      console.error('Failed to submit report:', error);
      alert('Failed to submit report: ' + (error.message || 'Unknown error'));
    }
  };

  const handleOpenModal = (report: any) => {
    // Create a copy of the report to avoid modifying the original
    const reportCopy = { ...report };

    // If machineId is missing but machine is present, use machine as machineId
    if (!reportCopy.machineId && reportCopy.machine) {
      reportCopy.machineId = reportCopy.machine;
    }

    setEditableReport(reportCopy);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditableReport(null); // Optionally reset the editable report
  };

  const handleEditReport = (e: any) => {
    const { name, value } = e.target;
    setEditableReport({ ...editableReport, [name]: value });

    // Specifically for location, to trigger machine list update
    if (name === 'location') {
      setEditableReport(prev => ({ ...prev, machineId: '', machineName: '', [name]: value }));
    }

    // Update machineName when machineId changes
    if (name === 'machineId') {
      const machine = machinesForEditReport.find(m => m.id === value);
      const machineName = machine ? machine.name : value;
      setEditableReport(prev => ({ ...prev, machineId: value, machineName }));
    }
  };

  const handleSubmitChanges = async () => {
    // Ensure machineName is set correctly
    if (editableReport.machineId && !editableReport.machineName) {
      const machine = machinesForEditReport.find(m => m.id === editableReport.machineId);
      if (machine) {
        editableReport.machineName = machine.name;
      } else {
        editableReport.machineName = editableReport.machineId;
      }
    }

    try {
      // Check if the ID is a valid UUID format
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(editableReport.id);

      if (!isValidUUID) {
        console.error('Cannot update report with non-UUID ID:', editableReport.id);
        alert('This report has an invalid ID format and cannot be updated. Please create a new report instead.');
        return;
      }

      // Set status to 'Up' if there's an end date
      const reportStatus = editableReport.endDate ? 'Up' : editableReport.status || 'Down';



      // If it's a valid UUID, proceed with the API call
      const { data, error } = await supabase
        .from('reports')
        .update({
          report_data: editableReport,
          location: editableReport.location,
          date: editableReport.date,
          end_date: editableReport.endDate,
          machine: editableReport.machineId,
          status: reportStatus,
          type: editableReport.cause
        })
        .eq('id', editableReport.id);

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }



      // Refresh the reports list to get the latest data from the server
      await fetchReports();

      setModalOpen(false);
      setSuccessMessage('Report updated successfully.');
      setSuccessDialogOpen(true);
      setTimeout(() => {
        setSuccessDialogOpen(false);
      }, 1500);
    } catch (error) {
      console.error('Failed to update report:', error);
      alert('Failed to update report: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteReport = async () => {
    try {
      // Check if the ID is a valid UUID format
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(editableReport.id);

      if (!isValidUUID) {
        console.error('Cannot delete report with non-UUID ID:', editableReport.id);
        alert('This report has an invalid ID format and cannot be deleted from the database.');
        return;
      }

      // Open the delete confirmation dialog
      setDeleteDialogOpen(true);
    } catch (error) {
      console.error('Error preparing to delete report:', error);
      alert('Error preparing to delete report: ' + (error.message || 'Unknown error'));
    }
  };

  const confirmDeleteReport = async () => {
    try {


      // If it's a valid UUID, proceed with the API call
      const { data, error } = await supabase
        .from('reports')
        .delete()
        .eq('id', editableReport.id);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }



      // Refresh the reports list to get the latest data from the server
      await fetchReports();

      setDeleteDialogOpen(false);
      handleCloseModal();
      setSuccessMessage('Report deleted successfully.');
      setSuccessDialogOpen(true);
      setTimeout(() => {
        setSuccessDialogOpen(false);
      }, 1500);
    } catch (error) {
      console.error('Failed to delete report:', error);
      alert('Failed to delete report: ' + (error.message || 'Unknown error'));
      setDeleteDialogOpen(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      startDate: '',
      endDate: '',
      assignee: '',
      status: '',
    });
    fetchReports(); // Optionally, instantly fetch reports after clearing filters to reset the view
  };

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: '900px',
    bgcolor: theme.palette.mode === 'dark' ? '#383838' : 'background.paper',
    borderRadius: '12px',
    boxShadow: 24,
    p: 0,
    maxHeight: '90vh',
    overflow: 'auto',
  };

  const handleCloseRequiredFieldDialog = () => {
    setIsRequiredFieldDialogOpen(false);
  };

  return (
    <div>
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="text.primary">
          Reports
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          View and manage machine downtime reports and maintenance records.
        </Typography>
      </Box>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: theme.palette.mode === 'dark' ? '#333' : '#f5f5f5',
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <TextField
          placeholder="Search..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            flexGrow: 1,
            '& .MuiOutlinedInput-root': {
              bgcolor: theme.palette.mode === 'dark' ? '#424242' : '#fff',
              borderRadius: 2
            }
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
          }}
        />
        <Button
          variant="contained"
          startIcon={<EditIcon />} // Using EditIcon as proxy for 'Create Ticket' icon from screenshot
          onClick={() => setCreateModalOpen(true)}
          sx={{
            borderRadius: 2,
            textTransform: 'none'
          }}
        >
          Create Report
        </Button>
        <Button
          variant="outlined"
          startIcon={<SearchIcon />} // Using SearchIcon as filter icon proxy or importa FilterListIcon
          onClick={() => setFilterOpen(true)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: theme.palette.mode === 'dark' ? '#424242' : '#fff',
            color: theme.palette.text.primary,
            borderColor: theme.palette.divider
          }}
        >
          Filters
        </Button>
      </Paper>

      {/* Create Report Modal */}
      <Dialog
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px', // Identifying with modern dialog style
          }
        }}
      >
        <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">Create New Report</Typography>
          <IconButton onClick={() => setCreateModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Location</InputLabel>
              <Select value={selectedLocation} label="Location" onChange={(e) => setSelectedLocation(e.target.value)} fullWidth>
                {locations.map((loc) => (
                  <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Machine</InputLabel>
              <Select value={machine} label="Machine" onChange={(e) => setMachine(e.target.value)} fullWidth>
                {machinesForNewReport.map((machine) => (
                  <MenuItem key={machine.id} value={machine.id}>{machine.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Assignee</InputLabel>
              <Select value={assignee} label="Assignee" onChange={(e) => setAssignee(e.target.value)} fullWidth>
                {assignees.map((assignee) => (
                  <MenuItem key={assignee} value={assignee}>{assignee}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Cause</InputLabel>
              <Select value={cause} label="Cause" onChange={(e) => setCause(e.target.value)} fullWidth>
                {causes.map((cause) => (
                  <MenuItem key={cause} value={cause}>{cause}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <TextField label="Description" variant="outlined" multiline rows={4} fullWidth value={description} onChange={(e) => setDescription(e.target.value)} sx={{ mb: 2 }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel shrink htmlFor="startDate">Start Date</InputLabel>
              <TextField id="startDate" type="date" fullWidth value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </FormControl>
            <FormControl variant="outlined" fullWidth>
              <InputLabel shrink htmlFor="endDate">End Date</InputLabel>
              <TextField id="endDate" type="date" fullWidth value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </FormControl>
            <TextField label="PO Number" variant="outlined" fullWidth value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
            <TextField label="Service Number" variant="outlined" fullWidth value={serviceNumber} onChange={(e) => setServiceNumber(e.target.value)} />
            <TextField label="Ticket Number (URL)" variant="outlined" fullWidth value={ticketNumber} onChange={(e) => setTicketNumber(e.target.value)} />
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateModalOpen(false)} variant="outlined" sx={{ borderRadius: 3 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => { handleSubmit(); setCreateModalOpen(false); }} sx={{ borderRadius: 3 }}>
            Submit Report
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Report Modal - Restored */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            minHeight: '70vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle component="div" sx={{ pb: 2, pt: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
              Edit Report
            </Typography>
            <IconButton edge="end" color="inherit" onClick={() => setModalOpen(false)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Location</InputLabel>
                <Select
                  name="location"
                  value={editableReport?.location || ''}
                  onChange={handleEditReport}
                  label="Location"
                >
                  {locations.map((loc) => (
                    <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Machine</InputLabel>
                <Select
                  name="machineId"
                  value={editableReport?.machineId || ''}
                  onChange={handleEditReport}
                  label="Machine"
                >
                  {machinesForEditReport.map((machine) => (
                    <MenuItem key={machine.id} value={machine.id}>{machine.name}</MenuItem>
                  ))}
                  {/* If the current machineId is not in the list, add it as an option */}
                  {editableReport?.machineId &&
                    !machinesForEditReport.some(m => m.id === editableReport.machineId) && (
                      <MenuItem value={editableReport.machineId}>
                        {editableReport.machineId}
                      </MenuItem>
                    )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel>Assignee</InputLabel>
                <Select
                  name="assignee"
                  value={editableReport?.assignee || ''}
                  onChange={handleEditReport}
                  label="Assignee"
                >
                  {assignees.map((assignee) => (
                    <MenuItem key={assignee} value={assignee}>{assignee}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel>Cause</InputLabel>
                <Select
                  name="cause"
                  value={editableReport?.cause || ''}
                  onChange={handleEditReport}
                  label="Cause"
                >
                  {causes.map((cause) => (
                    <MenuItem key={cause} value={cause}>{cause}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" variant="outlined" name="description" multiline rows={4} fullWidth value={editableReport?.description || ''} onChange={handleEditReport} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="PO Number" variant="outlined" name="poNumber" fullWidth value={editableReport?.poNumber || ''} onChange={handleEditReport} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Service Number" variant="outlined" name="serviceNumber" fullWidth value={editableReport?.serviceNumber || ''} onChange={handleEditReport} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Ticket Number" variant="outlined" name="ticketNumber" fullWidth value={editableReport?.ticketNumber || ''} onChange={handleEditReport} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField InputLabelProps={{ shrink: true }} label="Start Date" variant="outlined" name="date" type="date" fullWidth value={editableReport?.date || ''} onChange={handleEditReport} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField InputLabelProps={{ shrink: true }} label="End Date" variant="outlined" name="endDate" type="date" fullWidth value={editableReport?.endDate || ''} onChange={handleEditReport} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={() => setModalOpen(false)} variant="outlined" sx={{ borderRadius: 3 }}>
            Cancel
          </Button>
          <Button onClick={handleSubmitChanges} variant="contained" color="primary" startIcon={<EditIcon />} sx={{ borderRadius: 3 }}>
            Save Changes
          </Button>
          <Button onClick={handleDeleteReport} variant="contained" color="error" startIcon={<DeleteIcon />} sx={{ borderRadius: 3 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} aria-labelledby="filter-dialog-title" maxWidth="sm" fullWidth={true}>
        <DialogTitle id="filter-dialog-title" component="div" sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" component="div">
              Filter Reports
            </Typography>
            <IconButton edge="end" color="inherit" onClick={() => setFilterOpen(false)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Location</InputLabel>
            <Select
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              label="Location"
            >
              {locations.map((loc) => (
                <MenuItem key={loc} value={loc}>{loc}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Resolved">Resolved</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Assignee</InputLabel>
            <Select
              value={filters.assignee}
              onChange={(e) => setFilters({ ...filters, assignee: e.target.value })}
              label="Assignee"
            >
              <MenuItem value="">All</MenuItem>
              {assignees.map((assignee) => (
                <MenuItem key={assignee} value={assignee}>{assignee}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Start Date"
            type="date"
            fullWidth
            margin="dense"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            fullWidth
            margin="dense"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFilterOpen(false)} variant="outlined">Cancel</Button>
          <Button onClick={() => setFilterOpen(false)} variant="contained" color="primary">Apply</Button>
          <Button
            onClick={() => {
              setFilters({ location: '', startDate: '', endDate: '', assignee: '', status: '' });
            }}
            color="warning"
            variant="outlined"
          >
            Clear Filters
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer component={Paper}>
        <Table aria-label="simple table">
          <TableHead sx={tableHeadStyle}>
            <TableRow>
              <TableCell>Location</TableCell>
              <TableCell>Machine</TableCell>
              <TableCell>Cause</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Assignee</TableCell>
              <TableCell>PO Number</TableCell>
              <TableCell>Service Number</TableCell>
              <TableCell>Ticket Number</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentReports.map((report) => {
              const reportStatus = report.endDate ? "Resolved" : report.date ? "In Progress" : "Pending";
              const { displayPart, isValid } = getTicketDisplayAndLink(report.ticketNumber);
              return (
                <TableRow key={report.id} hover>
                  <TableCell>{report.location}</TableCell>
                  <TableCell>{report.machineName}</TableCell>
                  <TableCell>{report.cause}</TableCell>
                  <TableCell>
                    <div className="truncate-text">
                      {report.description}
                    </div>
                  </TableCell>
                  <TableCell>{report.date}</TableCell>
                  <TableCell>{report.endDate}</TableCell>
                  <TableCell>{report.assignee}</TableCell>
                  <TableCell>{report.poNumber}</TableCell>
                  <TableCell>{report.serviceNumber}</TableCell>
                  <TableCell>
                    {isValid ? (
                      <Link
                        href={report.ticketNumber}
                        target="_blank"
                        rel="noopener"
                        sx={{ color: theme.palette.mode === 'dark' ? '#90caf9' : theme.palette.primary.main }}
                      >
                        {displayPart}
                      </Link>
                    ) : (
                      displayPart
                    )}
                  </TableCell>
                  <TableCell>
                    {reportStatus === "In Progress" && <Chip label="In Progress" color="warning" />}
                    {reportStatus === "Resolved" && <Chip label="Resolved" color="success" />}
                    {reportStatus === "Pending" && <Chip label="Pending" />}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Report">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenModal(report);
                        }}
                        sx={{
                          color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main
                        }}
                      >
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={reports.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px',
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" component="div">
              Confirm Deletion
            </Typography>
            <IconButton edge="end" color="inherit" onClick={() => setDeleteDialogOpen(false)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this report for {editableReport?.machineName || editableReport?.machineId} at {editableReport?.location}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteReport}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isRequiredFieldDialogOpen}
        onClose={handleCloseRequiredFieldDialog}
        aria-labelledby="required-fields-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px',
          }
        }}
      >
        <DialogTitle id="required-fields-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" component="div">
              Required Fields Missing
            </Typography>
            <IconButton edge="end" color="inherit" onClick={handleCloseRequiredFieldDialog} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please fill in all required fields: Location, Machine, Assignee, Cause, and Start Date.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseRequiredFieldDialog} variant="contained" color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        aria-labelledby="success-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px',
          }
        }}
      >
        <DialogTitle id="success-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" component="div">
              Success
            </Typography>
            <IconButton edge="end" color="inherit" onClick={() => setSuccessDialogOpen(false)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {successMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSuccessDialogOpen(false)} variant="contained" color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Reports;